import type {
  PaymentClientAllocationInstruction,
  PaymentCorrection,
  PaymentException,
} from "./arRecoveryPaymentsApi";

/** Payment-scoped Payment Center panel datasets, stamped with the Payment they belong to. */
export type PaymentScopedPanels = {
  paymentId: string;
  corrections: PaymentCorrection[];
  exceptions: PaymentException[];
  clientAllocations: PaymentClientAllocationInstruction[];
};

export const EMPTY_PAYMENT_SCOPED_PANELS: PaymentScopedPanels = {
  paymentId: "",
  corrections: [],
  exceptions: [],
  clientAllocations: [],
};

export const PAYMENT_SCOPED_TABS = ["allocation", "operations", "exceptions"] as const;

export type PaymentScopedTab = (typeof PAYMENT_SCOPED_TABS)[number];

export function isPaymentScopedTab(tab: string): tab is PaymentScopedTab {
  return (PAYMENT_SCOPED_TABS as readonly string[]).includes(tab);
}

/** A newly selected Payment starts with empty panels so no prior Payment's records can render. */
export function beginPaymentScope(paymentId: string): PaymentScopedPanels {
  return { ...EMPTY_PAYMENT_SCOPED_PANELS, paymentId };
}

/** Commits a fetched dataset only under the Payment it was requested for, discarding late responses. */
export function commitPaymentScope(
  current: PaymentScopedPanels,
  paymentId: string,
  patch: Partial<Omit<PaymentScopedPanels, "paymentId">>,
): PaymentScopedPanels {
  const base = current.paymentId === paymentId ? current : beginPaymentScope(paymentId);
  return { ...base, paymentId, ...patch };
}

/**
 * Render guard. Panels are usable only while they belong to the Payment on screen, and each
 * record is re-checked against that Payment so an actionable control can never target another
 * Payment's correction or exception.
 */
export function resolvePaymentScope(
  panels: PaymentScopedPanels,
  selectedPaymentId: string | undefined,
): PaymentScopedPanels {
  if (!selectedPaymentId || panels.paymentId !== selectedPaymentId) return EMPTY_PAYMENT_SCOPED_PANELS;
  return {
    paymentId: selectedPaymentId,
    corrections: panels.corrections.filter((correction) => correction.originalPaymentId === selectedPaymentId),
    exceptions: panels.exceptions.filter((exception) => exception.paymentId === selectedPaymentId),
    clientAllocations: panels.clientAllocations,
  };
}

/** Company-wide exception feeds are narrowed to the selected Payment before they are stored. */
export function selectPaymentExceptions(exceptions: PaymentException[], paymentId: string) {
  return exceptions.filter((exception) => exception.paymentId === paymentId);
}
